/**
 * Profitability calculation utilities to ensure consistency between frontend and backend.
 *
 * - Form (Add/Edit rate): user enters **markup** (margen sobre coste).
 *   Markup = (Bill rate − Cost) / Cost; Bill rate = Cost × (1 + Markup / 100).
 * - Below the markup field, "Min profit margin" is displayed as read-only info:
 *   Margin = (Bill rate − Cost) / Bill rate.
 * - Backend stores markup in markup_percentage_cents / profitability_percentage_cents (30% = 3000).
 */

export const convertProfitabilityToCents = (profitabilityPercentage: number): number => {
  return Math.round(profitabilityPercentage * 100)
}

export const convertCentsToPercentage = (profitabilityCents: number): number => {
  return profitabilityCents / 100
}

const calculatePercentage = (numerator: number, denominator: number): string => {
  if (denominator === 0) return ''
  return ((numerator / denominator) * 100).toFixed(2)
}

/** Margin on price (read-only display): (bill - cost) / bill × 100. */
export const calculateMarginPercentage = (costRate: number, billRate: number): string => {
  const profit = billRate - costRate
  if (profit === 0) return '0'
  if (billRate === 0) return '-100.00'
  return calculatePercentage(profit, billRate)
}

/** Markup on cost (form field): (bill - cost) / cost × 100. */
export const calculateMarkupPercentage = (costRate: number, billRate: number): string => {
  if (costRate === 0) return '0'
  return calculatePercentage(billRate - costRate, costRate)
}

/** Bill rate from markup % (form): cost × (1 + markup/100). Used when user edits the markup field. */
export const calculateBillRateFromMarkup = (costRate: number, markupPercentage: number): string => {
  const calculatedBillRate = costRate * (1 + markupPercentage / 100)
  return calculatedBillRate.toFixed(2)
}

/** Margin on price in cents (read-only display below markup field). 30% => 3000. */
export const getMarginPercentageCents = (billRateCents: number, costRateCents: number): number => {
  if (billRateCents === 0) return 0
  return Math.round(((billRateCents - costRateCents) / billRateCents) * 10_000)
}
